import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { API, RUBRICS, SHORT_LABELS, FALLBACK_ARTICLES } from "@/data/content";
import { fmt } from "@/shop/api";

const rubricTitle = (id) => RUBRICS.find((r) => r.id === id)?.title ?? id;

export default function Editoria({ filter, onFilterChange }) {
  const [active, setActive] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!active?.episode) {
      setRelated([]);
      return;
    }
    fetch(`${API}/shop/related/${active.episode}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRelated(Array.isArray(d) ? d : []))
      .catch(() => setRelated([]));
  }, [active?.episode]);

  const { data: remote = [] } = useQuery({
    queryKey: ["articles"],
    queryFn: async () => {
      const res = await fetch(`${API}/articles`);
      if (!res.ok) throw new Error("fetch failed");
      return res.json();
    },
    staleTime: 60_000,
  });

  const articles = remote.length ? remote : FALLBACK_ARTICLES;
  const list =
    filter === "tutte"
      ? articles
      : articles.filter((a) => a.rubric === filter);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section
      id="numeri"
      data-testid="numeri-section"
      className="bg-ink-surface border-t border-ink-border px-5 md:px-10 py-20 md:py-32"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10 md:mb-14">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-ember mb-4"
          >
            In riva — numeri in uscita
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-light text-3xl md:text-5xl tracking-tight"
          >
            Storie dall'<span className="italic text-ember">altra sponda</span>
          </motion.h2>
        </div>

        <div className="flex flex-wrap gap-2">
          {["tutte", ...RUBRICS.map((r) => r.id)].map((id) => (
            <button
              key={id}
              data-testid={`filter-chip-${id}`}
              onClick={() => onFilterChange(id)}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-all duration-300 ${
                filter === id
                  ? "bg-ember text-ink border-ember"
                  : "border-ink-border text-mist hover:text-bone hover:border-faint"
              }`}
            >
              {SHORT_LABELS[id]}
            </button>
          ))}
        </div>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7"
        data-testid="articles-grid"
      >
        <AnimatePresence mode="popLayout">
          {list.map((a, i) => {
            const big = filter === "tutte" && i === 0;
            return (
              <motion.article
                layout
                key={a.id ?? a.episode}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setActive(a)}
                data-testid={`article-card-${a.id ?? a.episode}`}
                className={`group cursor-pointer h-full flex flex-col border border-ink-border bg-ink overflow-hidden hover:border-faint transition-colors duration-500 ${
                  big ? "md:col-span-2 lg:row-span-2" : ""
                }`}
              >
                <div className={`relative overflow-hidden ${big ? "aspect-[16/10] lg:aspect-[16/9]" : "aspect-[4/3]"}`}>
                  <img
                    src={a.image}
                    alt={a.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/20" />
                  <span className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.25em] bg-ink/70 backdrop-blur-sm text-bone px-3 py-1.5 border border-bone/10">
                    {a.episode} — {rubricTitle(a.rubric)}
                  </span>
                  <span className="absolute bottom-4 right-4 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-bone/80">
                    <Play size={11} strokeWidth={1.5} /> {a.duration}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-5 md:p-7">
                  <h3
                    className={`font-serif font-light tracking-tight leading-snug ${
                      big ? "text-2xl md:text-4xl" : "text-xl md:text-2xl"
                    } group-hover:text-ember transition-colors duration-500`}
                  >
                    {a.title}
                  </h3>
                  <p className={`mt-3 text-mist leading-relaxed ${big ? "text-sm md:text-base" : "text-xs md:text-sm"}`}>
                    {a.dek}
                  </p>
                  <div className="mt-auto pt-5 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.25em] text-faint">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={11} strokeWidth={1.5} /> {a.location}
                    </span>
                    <span>{a.author}</span>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="article-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-ink/85 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-ink-card border border-ink-border"
            >
              <button
                data-testid="modal-close"
                onClick={() => setActive(null)}
                aria-label="Chiudi"
                className="absolute top-4 right-4 z-10 p-2.5 bg-ink/70 backdrop-blur-sm border border-bone/15 text-bone hover:text-ember hover:border-ember transition-colors rounded-full"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
              <div className="aspect-video overflow-hidden">
                <img src={active.image} alt={active.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-6 md:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-4">
                  {active.episode} — {rubricTitle(active.rubric)} — {active.duration}
                </p>
                <h3 className="font-serif font-light text-2xl md:text-4xl tracking-tight leading-tight">
                  {active.title}
                </h3>
                <p className="mt-5 text-sm md:text-base text-mist leading-relaxed">{active.dek}</p>
                <div className="mt-8 grid grid-cols-2 gap-4 border-t border-ink-border pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
                  <span>
                    Luogo
                    <span className="block mt-1.5 text-bone normal-case tracking-normal font-serif text-sm">
                      {active.location}
                    </span>
                  </span>
                  <span>
                    A cura di
                    <span className="block mt-1.5 text-bone normal-case tracking-normal font-serif text-sm">
                      {active.author}
                    </span>
                  </span>
                </div>
                <button
                  data-testid="modal-watch"
                  onClick={() =>
                    toast.info("Il video va online col primo numero. Iscriviti al Diario delle Acque.", {
                      icon: "✳",
                    })
                  }
                  className="mt-8 inline-flex items-center gap-3 bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-3.5 rounded-full hover:bg-ember-bright transition-colors duration-300"
                >
                  <Play size={13} strokeWidth={2} /> Guarda l'episodio
                </button>
                {related.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8 border-t border-ink-border pt-6"
                    data-testid="modal-related-products"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-4">
                      Oggetti da questa storia
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {related.map((p) => (
                        <Link
                          key={p.id}
                          to={
                            p.product_type === "archive"
                              ? `/shop/archive/${p.slug}`
                              : p.product_type === "edition"
                                ? `/shop/editions/${p.slug}`
                                : `/shop/selected-tackle/${p.brand_slug || "-"}/${p.slug}`
                          }
                          onClick={() => {
                            setActive(null);
                            window.posthog?.capture("related_product_click", {
                              episode: active?.episode,
                              sku: p.sku,
                            });
                          }}
                          className="group border border-ink-border overflow-hidden hover:border-faint transition-colors"
                        >
                          <div className="aspect-square overflow-hidden">
                            <img
                              src={p.images?.[0] || "/photos/tre-canne.jpg"}
                              alt={p.name}
                              loading="lazy"
                              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          </div>
                          <div className="p-3">
                            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-faint">{p.brand || p.manufacturer}</p>
                            <p className="font-serif text-xs mt-1 group-hover:text-ember transition-colors leading-snug">{p.name}</p>
                            <p className="font-mono text-[10px] mt-1.5 text-mist">{fmt(p.price)}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}